import { validateDevice } from '../../schemas/deviceSchema';

export const updateDashboardUnits = async (
  updateType = 'all',
  dataInput,
  devicesDataInput,
  dashboardUnitsDataInput,
  unitsDataInput,
  unitsFirestoreDataInput,
  currentData,
  currentDevicesData,
  currentDashboardUnitsData,
  currentUnitsData,
  currentUnitsFirestoreData,
  dashboardUnitsComplete
) => {
  // Check if each parameter is undefined, and if so, use current data
  const dataChecked = dataInput !== undefined ? dataInput : currentData;
  const devicesDataChecked = devicesDataInput !== undefined ? devicesDataInput : currentDevicesData;
  const dashboardUnitsDataChecked = dashboardUnitsDataInput !== undefined ? dashboardUnitsDataInput : currentDashboardUnitsData;
  const unitsDataChecked = unitsDataInput !== undefined ? unitsDataInput : currentUnitsData;
  const unitsFirestoreDataChecked = unitsFirestoreDataInput !== undefined ? unitsFirestoreDataInput : currentUnitsFirestoreData;

  let groupedDataByUnit = {};

  if (updateType === "all" || updateType === "issues") {
    if (devicesDataChecked === undefined || devicesDataChecked.length === 0) {
      return { dashboardUnits: {}, groupedDataByUnit: {}, totalDailyUses: 0, totalDailyUptime: 0 };
    }

    // Initialize groupedDataByUnit with keys from dashboardUnitsDataChecked
    dashboardUnitsDataChecked.forEach(unit => {
      groupedDataByUnit[unit.unitId] = null;
    });

    dataChecked.forEach((item) => {
      const key = item.Installation.Unit_ID;
    
      // Skip if the key does not exist in groupedDataByUnit
      if (!(key in groupedDataByUnit)) {
        return;
      }
    
      if (item.createdAt === undefined) {
        console.log('No createdAt timestamp:', item);
      }

      if (groupedDataByUnit[key] === null) {
        groupedDataByUnit[key] = item;
      } else if (item.createdAt > groupedDataByUnit[key].createdAt) {
        // Keep the most recent issue per unit
        groupedDataByUnit[key] = item;
      }
    });
  }

  let dashboardUnits = { ...dashboardUnitsComplete };
  const unitIdToKey = {};

  let usesCounter = 0;
  let dailyUptimeSum = 0.0;
  let activeUnitsCount = 0;

  if (updateType === "all" || updateType === "units") {
    dashboardUnitsDataChecked.forEach((unit) => {
      if (!dashboardUnits[unit.unitId]) {
        dashboardUnits[unit.unitId] = {
          organization: "",
          center: "",
          unit: unit.unitId,
          unit_status: "active",
          daily_unit_use: 0,
          total_kpod_use: 0,
          kpod_id: "",
          display_id: "",
          isUnitDisconnected: false,
          description: "",
          rosterActive: false,
          lastLoginTimestamp: "-",
          lastInitTimestamp: "-",
          isSmartActionsEnabled: false,
          isUnitStuck: false,
          isSit: false,
          clogged: false,
          dailyUptime: 0,
          hasErrors: false,
        };
      }
    });

    for (const key in dashboardUnits) unitIdToKey[dashboardUnits[key].unit] = key;

    unitsDataChecked.forEach((unitData) => {
      const matchingUnitKey = unitIdToKey[unitData.unitID];
      if (!matchingUnitKey) return;

      const {
        unitID,
        cartridgeUsageCounter,
        status,
        devices,
        isUnitDisconnected,
        isUnitStuck,
        description,
        organizationName,
        centerName,
        rosterActive,
        lastLoginTimestamp,
        lastInitTimestamp,
        dailyUsageCounter,
        isSit,
        clogged,
        dailyUptime,
        lastScoreTimestamp,
        statusInfo,
      } = unitData;

      // Only count daily usage and uptime for non-backend units
      if (unitID !== 'backend') {
        usesCounter += dailyUsageCounter || 0;
        if (status === "active") {
          // Convert dailyUptime to number
          let uptimeValue = 0;
          if (typeof dailyUptime === 'number') {
            uptimeValue = dailyUptime;
          } else if (typeof dailyUptime === 'string' && dailyUptime !== "-") {
            uptimeValue = parseFloat(dailyUptime) || 0;
          }
          
          dailyUptimeSum += uptimeValue;
          activeUnitsCount++;
        }
      }

      if (devices) {
        dashboardUnits[matchingUnitKey].organization = organizationName || "-";
        dashboardUnits[matchingUnitKey].center = centerName || "-";
      }
      dashboardUnits[matchingUnitKey].isUnitDisconnected = isUnitDisconnected || false;
      dashboardUnits[matchingUnitKey].rosterActive = rosterActive || false;
      dashboardUnits[matchingUnitKey].isUnitStuck = isUnitStuck || false;
      dashboardUnits[matchingUnitKey].lastLoginTimestamp = lastLoginTimestamp || "-";
      dashboardUnits[matchingUnitKey].lastInitTimestamp = lastInitTimestamp || "-";

      dashboardUnits[matchingUnitKey].unit_status = status || "";
      dashboardUnits[matchingUnitKey].description = description || "";
      dashboardUnits[matchingUnitKey].total_kpod_use = cartridgeUsageCounter || "-";
      dashboardUnits[matchingUnitKey].daily_unit_use = dailyUsageCounter === null ? 0 : dailyUsageCounter;
      dashboardUnits[matchingUnitKey].isSit = isSit || false;
      dashboardUnits[matchingUnitKey].clogged = clogged || false;
      dashboardUnits[matchingUnitKey].dailyUptime = dailyUptime || "-";
      dashboardUnits[matchingUnitKey].since_last_score = lastScoreTimestamp || "-";
      dashboardUnits[matchingUnitKey].statusInfo = statusInfo || "";
    });
  }

  for (const key in dashboardUnits) unitIdToKey[dashboardUnits[key].unit] = key;

  if (updateType === "all" || updateType === "issues") {
    // Fill additional details from groupedDataByUnit
    for (const key in groupedDataByUnit) {
      const matchingUnitKey = unitIdToKey[key];
      if (groupedDataByUnit[key] && matchingUnitKey) {
        const item = groupedDataByUnit[key];
        if (item) {
          const unit = dashboardUnits[matchingUnitKey];
          unit.time = item.createdAt || "-";
          if (unit.organization === "" || unit.center === "") {
            unit.organization = item.Installation.Organization || "-";
            unit.center = item.Installation.Center || "-";
          }
        }
      }
    }
  }

  if (updateType === "all" || updateType === "units") {
    const isSmartActionsEnabledMap = {};
    const scheduledWFMap = {};
    const postAssemblyCalibrationMap = {};
    const parametersMap = {};

    const firestoreDevicesMap = {};
    const waterReferenceTsMap = {};

    unitsFirestoreDataChecked.forEach(({ unitID, isSmartActionsEnabled, scheduledWF, postAssemblyCalibration, parameters, devices, water_reference_ts }) => {
      isSmartActionsEnabledMap[unitID] = isSmartActionsEnabled || false;
      scheduledWFMap[unitID] = scheduledWF || null;
      postAssemblyCalibrationMap[unitID] = postAssemblyCalibration || null;
      parametersMap[unitID] = parameters || null;
      firestoreDevicesMap[unitID] = devices || null;
      waterReferenceTsMap[unitID] = water_reference_ts || null;
    });

    // Convert `devicesDataChecked` into a lookup map
    const devicesInstallDateMap = {};
    devicesDataChecked
      .filter(device => {
        if (!validateDevice(device)) {
          console.warn('Invalid device in updateDashboardUnits:', device);
          return false;
        }
        return true;
      })
      .forEach(({ deviceID, installationDate }) => {
        devicesInstallDateMap[deviceID] = installationDate;
      });

    // Single iteration through `dashboardUnits`
    for (const key in dashboardUnits) {
      const unit = dashboardUnits[key];

      // Update isSmartActionsEnabled
      if (unit.unit in isSmartActionsEnabledMap) {
        unit.isSmartActionsEnabled = isSmartActionsEnabledMap[unit.unit];
      }

      // Update scheduledWF
      if (unit.unit in scheduledWFMap) {
        unit.scheduledWF = scheduledWFMap[unit.unit];
      }

      // Update postAssemblyCalibration
      if (unit.unit in postAssemblyCalibrationMap) {
        unit.postAssemblyCalibration = postAssemblyCalibrationMap[unit.unit];
      }

      // Update parameters
      if (unit.unit in parametersMap) {
        unit.parameters = parametersMap[unit.unit];
      }

      // Update kpod_id/display_id from the Firestore devices object (source of truth for device swaps)
      if (firestoreDevicesMap[unit.unit]) {
        unit.kpod_id = firestoreDevicesMap[unit.unit].kPodID || "-";
        unit.display_id = firestoreDevicesMap[unit.unit].displayID || "-";
      }

      // Update installation date
      if (devicesInstallDateMap[unit.kpod_id]) {
        unit.time_installed = devicesInstallDateMap[unit.kpod_id];
      }

      // Update last (properly taken) water reference timestamp
      if (waterReferenceTsMap[unit.unit]) {
        unit.water_reference_ts = waterReferenceTsMap[unit.unit];
      }

      // Handle backend-specific logic
      if (key === "backend") {
        unit.unit_status = "active";
        unit.daily_unit_use = 0;
        unit.organization = "-";
        unit.center = "-";
      }
    }
  }

  const meanDailyUptimePercentage = activeUnitsCount > 0
    ? Math.round((dailyUptimeSum / activeUnitsCount / 86400) * 100)
    : 0;

  return {
    dashboardUnits,
    groupedDataByUnit,
    totalDailyUses: usesCounter,
    totalDailyUptime: meanDailyUptimePercentage
  };
};